import { useEffect, useRef } from "react";
import { isArrowKey, takeKeyFromField } from "../listKeyboard";

interface PaymentMethodKeys {
  /** Модалът е отворен на десктоп, методите са заредени и няма слой отгоре. */
  enabled: boolean;
  /** Методите, до които стигат стрелките, в реда на екрана; празен — стрелките мълчат. */
  methodIds: number[];
  selectedMethodId: number | null;
  onMove: (methodId: number) => void;
  onEnter: () => void;
  onEscape: () => void;
}

/**
 * Клавиатурата на „Закрий" и „Плати избраните" — една за двата модала, за да
 * са 1:1: ↑/← и ↓/→ сменят метода в кръг, Enter закрива, Esc затваря (с
 * preventDefault — иначе минава и глобалното „назад"). Полетата — по общите
 * правила (`listKeyboard.ts`): докато се пише, клавишите са на полето; от
 * полетата за дадената сума (`LEAVE_FIELD_ON_ARROWS`) ↑ ↓ излизат и местят
 * метода, а полетата на „Смесено" (InputNumber) пазят всичко за себе си.
 * Callback-ите се четат от ref — слушателят не се пре-закача на всеки рендер.
 */
export const usePaymentMethodKeys = (keys: PaymentMethodKeys) => {
  const latest = useRef(keys);
  useEffect(() => {
    latest.current = keys;
  });

  const { enabled } = keys;
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!takeKeyFromField(event)) return;
      const current = latest.current;
      if (event.key === "Enter") {
        event.preventDefault();
        current.onEnter();
      } else if (event.key === "Escape") {
        event.preventDefault();
        current.onEscape();
      } else if (isArrowKey(event.key)) {
        event.preventDefault();
        const { methodIds, selectedMethodId } = current;
        if (methodIds.length === 0) return;
        const index = selectedMethodId === null ? -1 : methodIds.indexOf(selectedMethodId);
        const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
        const nextIndex = forward
          ? (index + 1) % methodIds.length
          : index <= 0
            ? methodIds.length - 1
            : index - 1;
        current.onMove(methodIds[nextIndex]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled]);
};
